import {
  JsonFormat,
  objectJsonFormat,
  stringEnumJsonFormat,
  symbolJsonFormat,
  symbolUnionJsonFormat,
} from "@redotech/json/format";
import { ManualLabelMode, StoreCreditType } from "./return-option-enums";

const creditTypeJsonFormat: JsonFormat<StoreCreditType> =
  stringEnumJsonFormat(StoreCreditType);

export const manualLabelModeJsonFormat: JsonFormat<ManualLabelMode> =
  stringEnumJsonFormat(ManualLabelMode);

/** Whether the mode rules out a Redo label, whoever then provides the label. */
export function isForcedManualLabelMode(mode: ManualLabelMode): boolean {
  return (
    mode === ManualLabelMode.MERCHANT_FORCE ||
    mode === ManualLabelMode.SHOPPER_FORCE
  );
}

/**
 * How an option offers the manual label. An option with no mode stored does not
 * offer one, so every reader goes through here rather than the field.
 */
export function manualLabelMode(option: {
  manual_label_mode?: ManualLabelMode | null;
}): ManualLabelMode {
  return option.manual_label_mode ?? ManualLabelMode.OFF;
}

/**
 * Return method
 */
export type ReturnOptionMethod =
  | ReturnOptionMethod.StoreCredit
  | ReturnOptionMethod.Exchange
  | ReturnOptionMethod.Refund
  | ReturnOptionMethod.Repair;

export namespace ReturnOptionMethod {
  export const STORE_CREDIT = Symbol("credit");
  export const EXCHANGE = Symbol("exchange");
  export const REFUND = Symbol("refund");
  export const REPAIR = Symbol("repair");

  /**
   * Store credit
   */
  export interface StoreCredit {
    type: typeof STORE_CREDIT;
    creditType: StoreCreditType;
  }

  export const creditJsonFormat: JsonFormat<StoreCredit> = objectJsonFormat(
    { type: symbolJsonFormat(STORE_CREDIT), creditType: creditTypeJsonFormat },
    {},
  );

  /**
   * Exchange
   */
  export interface Exchange {
    type: typeof EXCHANGE;
  }

  export const exchangeJsonFormat: JsonFormat<Exchange> = objectJsonFormat(
    { type: symbolJsonFormat(EXCHANGE) },
    {},
  );

  /**
   * Refund
   */
  export interface Refund {
    type: typeof REFUND;
  }

  export const refundJsonFormat: JsonFormat<Refund> = objectJsonFormat(
    { type: symbolJsonFormat(REFUND) },
    {},
  );

  export interface Repair {
    type: typeof REPAIR;
  }

  export const repairJsonFormat: JsonFormat<Repair> = objectJsonFormat(
    { type: symbolJsonFormat(REPAIR) },
    {},
  );
}

export const returnOptionMethodJsonFormat: JsonFormat<ReturnOptionMethod> =
  symbolUnionJsonFormat("type", "type", {
    [ReturnOptionMethod.STORE_CREDIT]: ReturnOptionMethod.creditJsonFormat,
    [ReturnOptionMethod.EXCHANGE]: ReturnOptionMethod.exchangeJsonFormat,
    [ReturnOptionMethod.REFUND]: ReturnOptionMethod.refundJsonFormat,
    [ReturnOptionMethod.REPAIR]: ReturnOptionMethod.repairJsonFormat,
  });
